import { useState, useRef, useEffect } from 'react';
import { Filter, Check, Info } from 'lucide-react';
import type { StatusFilterValue } from './StatusFilterDropdown.constants';

/** Status filter option configuration */
interface StatusOption {
  value: StatusFilterValue;
  label: string;
  tooltip?: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'due', label: 'Has due date' },
  { value: 'nodue', label: 'No due date' },
  { value: 'risk', label: 'At risk', tooltip: 'Only applies to deliverables with due dates' },
  { value: 'safe', label: 'On track', tooltip: 'Only applies to deliverables with due dates' },
];

export interface StatusFilterDropdownProps {
  /** Currently selected status filters */
  selected: Set<StatusFilterValue>;
  /** Callback when selection changes */
  onChange: (selected: Set<StatusFilterValue>) => void;
}

/**
 * Status filter dropdown molecule for filtering by due date and risk status
 *
 * Provides options for filtering deliverables by:
 * - Due date presence (has due date / no due date)
 * - Risk status (at risk / on track)
 */
export function StatusFilterDropdown({ selected, onChange }: StatusFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleOption = (value: StatusFilterValue) => {
    const newSelected = new Set(selected);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    onChange(newSelected);
  };

  const selectAll = () => onChange(new Set(STATUS_OPTIONS.map((o) => o.value)));
  const deselectAll = () => onChange(new Set());

  const getLabel = () => {
    if (selected.size === 0) return 'None';
    if (selected.size === STATUS_OPTIONS.length) return 'All Status';
    if (selected.size === 1) {
      const value = Array.from(selected)[0];
      return STATUS_OPTIONS.find((o) => o.value === value)?.label ?? 'Status';
    }
    return `${selected.size} statuses`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center gap-2 rounded-md border border-border-default bg-bg-elevated px-3 py-1.5 text-sm
          text-text-primary transition-colors
          hover:bg-bg-base
          focus:ring-2 focus:ring-primary-500 focus:outline-hidden
        "
      >
        <Filter className="size-4 text-text-muted" />
        <span>{getLabel()}</span>
      </button>

      {isOpen && (
        <div
          className="
            absolute left-0 z-50 mt-2 flex w-64 flex-col overflow-hidden rounded-lg border border-border-default
            bg-bg-elevated shadow-lg
          "
        >
          {/* Select All / Deselect All */}
          <div className="flex gap-2 border-b border-border-default px-3 py-2">
            <button
              onClick={selectAll}
              className="
                text-xs text-action-primary
                hover:underline
              "
            >
              Select All
            </button>
            <span className="text-text-disabled">|</span>
            <button
              onClick={deselectAll}
              className="
                text-xs text-action-primary
                hover:underline
              "
            >
              Deselect All
            </button>
          </div>

          {/* Options List */}
          <div className="py-1">
            {STATUS_OPTIONS.map((option, index) => {
              const isSelected = selected.has(option.value);
              // Add separator between due date options and risk options
              const showSeparator = index === 2;
              return (
                <div key={option.value}>
                  {showSeparator && <div className="my-1 border-t border-border-default" />}
                  <label
                    className="
                      flex cursor-pointer items-center gap-2 px-3 py-2
                      hover:bg-bg-base
                    "
                  >
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOption(option.value)}
                        className="sr-only"
                      />
                      <div
                        className={`
                          flex size-4 items-center justify-center rounded-sm border-2 transition-colors
                          ${isSelected ? 'border-action-primary bg-action-primary' : `border-border-strong`}
                        `}
                      >
                        {isSelected && <Check className="size-3 text-text-inverse" />}
                      </div>
                    </div>
                    <span className="flex-1 text-sm text-text-primary">{option.label}</span>
                    {option.tooltip && (
                      <div className="group relative" title={option.tooltip}>
                        <Info className="size-3.5 text-text-subtle" />
                      </div>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
